import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useRequestMagicLink } from '../../hooks/useMagicLink'
import { signIn } from '../../services/api'
import { setAuthToken } from '../../utils/authToken'

// OmniAuth route mounted by Devise at /api/v1/auth/auth/:provider — the
// double "auth" segment is intentional (OmniAuth's own /auth/:provider
// suffix on top of our Devise mount path). Mirrors SocialLoginButtons.tsx.
// All OAuth sign-ins land on /portal/dashboard via OAuthCallbackPage's
// default; query params don't round-trip through the OAuth handshake
// without going through omniauth.origin, which we don't need yet.
const API_BASE   = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api/v1') as string
const OAUTH_BASE = `${API_BASE}/auth/auth`
function oauthHref(provider: 'google_oauth2' | 'facebook' | 'apple') {
  return `${OAUTH_BASE}/${provider}`
}

export default function PortalSignInPage() {
  const [email, setEmail]             = useState('')
  const [password, setPassword]       = useState('')
  const [usePassword, setUsePassword] = useState(false)
  const [submitted, setSubmitted]     = useState(false)
  const [pwError, setPwError]         = useState('')
  const [pwPending, setPwPending]     = useState(false)
  const [params]                      = useSearchParams()
  const returnTo                      = params.get('return') || '/portal/dashboard'
  const navigate                      = useNavigate()

  const request = useRequestMagicLink()

  const onMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    await request.mutateAsync(email)
    setSubmitted(true)
  }

  const onPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwError('')
    setPwPending(true)
    try {
      const { token } = await signIn(email, password)
      setAuthToken(token)
      navigate(returnTo, { replace: true })
    } catch {
      setPwError('Invalid email or password.')
    } finally {
      setPwPending(false)
    }
  }

  return (
    <div className="min-h-screen bg-nala-surface flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Wordmark */}
        <Link to="/" className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="w-9 h-9 rounded-xl bg-nala-accent text-nala-accentInk flex items-center justify-center font-fraunces font-semibold text-sm">
            N
          </div>
          <span className="font-fraunces text-[22px] font-semibold text-nala-ink">
            Nala<span className="text-nala-clay italic">.vet</span>
          </span>
        </Link>

        <div className="bg-white border border-nala-line rounded-3xl p-8 shadow-sm">
          {submitted ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-2xl bg-nala-soft flex items-center justify-center text-2xl mx-auto mb-5">📬</div>
              <h1 className="font-fraunces text-2xl font-[400] text-nala-ink mb-3">Check your email</h1>
              <p className="text-nala-mute leading-relaxed text-sm max-w-xs mx-auto">
                We sent a sign-in link to <strong className="text-nala-ink">{email}</strong>. It expires in 15 minutes.
              </p>
              <button
                className="mt-5 text-sm text-nala-clay hover:underline font-medium"
                onClick={() => setSubmitted(false)}
              >
                Try a different email
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-fraunces text-3xl font-[400] text-nala-ink mb-1">
                {usePassword ? 'Sign in' : 'Welcome back.'}
              </h1>

              {/* Social sign-in row sits above whichever email-shaped method
                  the visitor has chosen. We render the same three providers
                  on both the magic-link and password tabs so users don't
                  have to dig under "use password instead" to find Google. */}
              <div className="mt-5 mb-5 flex flex-col gap-2.5">
                <a
                  href={oauthHref('google_oauth2')}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-nala-line bg-white text-sm font-medium text-nala-ink hover:bg-nala-surface transition"
                >
                  <GoogleGlyph />
                  <span>Continue with Google</span>
                </a>
                <a
                  href={oauthHref('apple')}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-nala-line bg-white text-sm font-medium text-nala-ink hover:bg-nala-surface transition"
                >
                  <AppleGlyph />
                  <span>Continue with Apple</span>
                </a>
                <a
                  href={oauthHref('facebook')}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-nala-line bg-white text-sm font-medium text-nala-ink hover:bg-nala-surface transition"
                >
                  <FacebookGlyph />
                  <span>Continue with Facebook</span>
                </a>
              </div>

              <div className="relative mb-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-nala-line" />
                </div>
                <div className="relative flex justify-center text-[11px]">
                  <span className="bg-white px-3 text-nala-mute uppercase tracking-wider">or with email</span>
                </div>
              </div>

              {!usePassword ? (
                <>
                  <p className="text-nala-mute text-sm mb-6">
                    Enter your email and we'll send you a one-tap sign-in link.
                  </p>

                  <form onSubmit={onMagicLink} className="space-y-4">
                    <NalaField label="Email">
                      <NalaInput
                        id="email"
                        type="email"
                        required
                        autoFocus
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </NalaField>

                    <button
                      type="submit"
                      disabled={request.isPending}
                      className="w-full bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold py-3 rounded-full transition disabled:opacity-60 text-sm"
                    >
                      {request.isPending ? 'Sending…' : 'Send sign-in link'}
                    </button>

                    {request.isError && (
                      <p className="text-sm text-red-600">Something went wrong. Please try again.</p>
                    )}
                  </form>

                  <div className="mt-5 pt-5 border-t border-nala-line text-center">
                    <button
                      className="text-sm text-nala-clay hover:underline font-medium"
                      onClick={() => setUsePassword(true)}
                    >
                      Sign in with password instead
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-nala-mute text-sm mb-6">Enter your email and password.</p>

                  <form onSubmit={onPassword} className="space-y-4">
                    <NalaField label="Email">
                      <NalaInput
                        id="pw-email"
                        type="email"
                        required
                        autoFocus
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </NalaField>

                    <NalaField label="Password">
                      <NalaInput
                        id="pw-password"
                        type="password"
                        required
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </NalaField>

                    <button
                      type="submit"
                      disabled={pwPending}
                      className="w-full bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold py-3 rounded-full transition disabled:opacity-60 text-sm"
                    >
                      {pwPending ? 'Signing in…' : 'Sign in'}
                    </button>

                    {pwError && <p className="text-sm text-red-600">{pwError}</p>}
                  </form>

                  <div className="mt-5 pt-5 border-t border-nala-line text-center">
                    <button
                      className="text-sm text-nala-clay hover:underline font-medium"
                      onClick={() => { setUsePassword(false); setPwError('') }}
                    >
                      ← Use a sign-in link instead
                    </button>
                  </div>
                </>
              )}

              <p className="text-xs text-nala-mute mt-6 text-center leading-relaxed">
                By continuing you agree to our terms. We only use your email to sign you in
                and send appointment reminders.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function NalaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-nala-mute uppercase tracking-wide mb-1.5">
        {label}
      </span>
      {children}
    </label>
  )
}

function NalaInput({
  id, type, required, autoFocus, autoComplete, value, onChange, placeholder,
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      id={id}
      type={type}
      required={required}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      value={value as string}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full rounded-xl border border-nala-line px-4 py-3 text-sm text-nala-ink focus:border-nala-accent focus:outline-none focus:ring-1 focus:ring-nala-accent placeholder:text-nala-line/80 transition"
    />
  )
}

// ─── Provider glyphs (inline SVG so we don't need an icon dep) ──────────────

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335" />
    </svg>
  )
}

function AppleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.39.07 2.35.74 3.15.8 1.19-.24 2.35-.93 3.62-.84 1.54.12 2.69.73 3.45 1.84-3.17 1.9-2.42 5.97.48 7.13-.57 1.53-1.32 3.04-2.7 3.95M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25" />
    </svg>
  )
}

function FacebookGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="#1877F2">
      <path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z" />
    </svg>
  )
}
